import React, { useState, useEffect, useContext, useRef } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useNavigate } from "react-router-dom";
import { Toast } from "../../Common/Toast-Snackbar/ToastSnackbar";
import Button from "../../Common/Button/Button";
import { DynamicContext } from "../../Context/DynamicContext";
import { post } from "../../helper/Api_helper";
import {
  VYLABS_BASE_URL,
  VYLABS_USER_DASHBOARD_URL,
  VERIFY_OTP,
  SEND_OTP,
  SIGNUP_URL,
  CREATE_TENANT,
} from "../../helper/url_helper";
import {
  TENANT_CORE_API_KEY,
  TENANT_OTP_API_KEY,
} from "../../Tenant-Config/TenantConfig";
import { TextField, Box } from "@mui/material";
import Cookies from "js-cookie";
import "./VerifyOtp.css";
import { getTimezone } from "../../Common/Time-Zone-Utils/timezoneUtils";
function VerifyOtp(props) {
  const {
    setSignupPassword,
    setSignupEmail,
    setSignupCompleted,
    setShowOtpPage,
  } = useContext(DynamicContext);
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [isResendDisabled, setIsResendDisabled] = useState(true);
  const [resendTimer, setResendTimer] = useState(300);
  const [email, setEmail] = useState("");
  const inputRefs = useRef([]);

  useEffect(() => {
    const tempEmail = sessionStorage.getItem("tempEmail");
    if (!tempEmail) {
      setShowOtpPage(false);
      return;
    }
    setEmail(tempEmail);
  }, [setShowOtpPage]);

  useEffect(() => {
    let interval = null;
    if (isResendDisabled && resendTimer > 0) {
      interval = setInterval(() => {
        setResendTimer((prevTimer) => prevTimer - 1);
      }, 1000);
    } else if (resendTimer === 0) {
      setIsResendDisabled(false);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isResendDisabled, resendTimer]);

  const formik = useFormik({
    initialValues: {
      otp: ["", "", "", "", "", ""].join(""),
    },
    validationSchema: Yup.object({
      otp: Yup.string()
        .required("Please enter the 6-digit OTP")
        .matches(/^\d{6}$/, "OTP must be exactly 6 digits"),
    }),
    onSubmit: (values) => {
      handleVerifyOTP(values.otp);
    },
  });

  const handleOtpChange = (index, value) => {
    if (!/^\d?$/.test(value)) return;

    const newOtp = formik.values.otp.split("");
    newOtp[index] = value;
    const otpString = newOtp.join("");

    formik.setFieldValue("otp", otpString);
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && !formik.values.otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasteData = e.clipboardData.getData("text");
    const numbers = pasteData.replace(/\D/g, "").slice(0, 6);

    if (numbers.length === 6) {
      formik.setFieldValue("otp", numbers);
      inputRefs.current[5]?.focus();
    }
  };  

  const handleVerifyOTP = async (otp) => {
    if (!otp || otp.length !== 6) {
      Toast("error", "Please enter a valid 6-digit OTP");
      return;
    }

    setIsLoading(true);
    try {
      const verifyResponse = await post(VYLABS_BASE_URL, VERIFY_OTP, {
          email,
          otp,
        },
        {
          headers: {
            "X-API-Key": TENANT_OTP_API_KEY,
          },
        },
      );
      if (verifyResponse === "otp verified") {
        const tempSignupData = sessionStorage.getItem("tempSignupData");
        if (tempSignupData) {
          await handleSignupCompletion();
        }
      }
    } catch (error) {
      console.error("OTP verification error:", error);
      if (error.response?.status === 400 || error.response?.status === 401) {
        Toast(
          "error",
          error.response?.data || "Invalid OTP. Please try again.",
        );
      } else {
        Toast("error", error.message || "Verification failed");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignupCompletion = async () => {
    try {
      const tempSignupData = JSON.parse(
        sessionStorage.getItem("tempSignupData"),
      );
      if (!tempSignupData) {
        throw new Error("Signup data not found");
      }
      const signupPayload = {
        email: tempSignupData.email,
        firstName: tempSignupData.firstname,
        lastName: tempSignupData.lastname,
        password: tempSignupData.password,
        phone: tempSignupData.phoneNumber,
        userType: "INTERNAL_USER",
        timezone: getTimezone(),
      };

      const signupResponse = await post(
        VYLABS_BASE_URL,
        SIGNUP_URL,
        signupPayload,
        {
          _skipAuth: true,
          headers: {
            "X-API-Key": TENANT_OTP_API_KEY,
          },
        },
      );

      if (signupResponse && signupResponse.access_token) {
        localStorage.setItem("access_token", signupResponse.access_token);
        localStorage.setItem("refresh_token", signupResponse.refresh_token);
        localStorage.setItem(
          "access_token_expires_at",
          signupResponse.access_token_expires_at,
        );

        const isAdmin =
          tempSignupData.companyName &&
          tempSignupData.companyName.trim() !== "";
        if (isAdmin) {
          const tempToken = signupResponse.access_token;
          try {
            await handleCreateTenant(tempSignupData.companyName, tempToken);
          } catch (tenantError) {
            console.error("Tenant creation failed:", tenantError);
          }
          sessionStorage.setItem(
            "adminSignupData",
            JSON.stringify({
              email: tempSignupData.email,
              companyName: tempSignupData.companyName,
              userId: signupResponse.user?.id,
              access_token: signupResponse.access_token,
              access_token_expires_at: signupResponse.access_token_expires_at,
              refresh_token: signupResponse.refresh_token,
              refresh_token_expires_at: signupResponse.refresh_token_expires_at,
            }),
          );
          sessionStorage.removeItem("tempSignupData");
          sessionStorage.removeItem("tempEmail");
          navigate("/select-plan");
        } else {
          setSignupEmail(tempSignupData.email);
          setSignupPassword(tempSignupData.password);
          sessionStorage.removeItem("tempSignupData");
          sessionStorage.removeItem("tempEmail");
          setSignupCompleted(true);
        }
      } else {
        throw new Error("Signup failed - no access token received");
      }
    } catch (error) {
      console.error("Signup completion error:", error);
      Toast("error", error?.response?.data || "Account creation failed");
      sessionStorage.removeItem("tempSignupData");
      sessionStorage.removeItem("tempEmail");
    }
  };

  const handleCreateTenant = async (companyName, accessToken) => {
    try {
      const requestConfig = {
        headers: {
          "X-API-Key": TENANT_CORE_API_KEY,
          ...(accessToken && { Authorization: `Bearer ${accessToken}` }),
        },
      };
      const response = await post(
        VYLABS_BASE_URL,
        CREATE_TENANT,
        {
          name: companyName,
          metadata: {},
        },
        requestConfig,
      );
      Toast(
        "success",
        "Admin account created successfully! Please select a plan to start your trial",
      );
      return response;
    } catch (error) {
      console.error("tenant creation response error>>>", error);
      throw error;
    }
  };

  const handleResendOTP = async () => {
    try {
      const otpResponse = await post(
        VYLABS_BASE_URL,
        SEND_OTP,
        { email },
        {
          headers: {
            "X-API-Key": TENANT_OTP_API_KEY,
          },
        },
      );

      if (otpResponse === "otp sent") {
        setIsResendDisabled(true);
        setResendTimer(30);
        Toast("success", "OTP resent to your email.");
      } else {
        Toast("error", "Failed to resend OTP");
      }
    } catch (error) {
      console.error("Resend OTP error:", error);
      Toast("error", "Failed to resend OTP");
    }
  };

  const handleBackToLogin = () => {
    setShowOtpPage(false);
    sessionStorage.removeItem("tempEmail");
    sessionStorage.removeItem("tempPassword");
  };

  return (
    <div className="otp-page">
      <div className="otp-main-container">
        <div className="otp-container">
          <h2 className="otp-heading">Verify OTP</h2>
          <h4 style={{ textAlign: "center" }}>
            Enter the 6-digit code sent to{" "}
            <span className="otp-email">{email}</span>
          </h4>

          <form onSubmit={formik.handleSubmit}>
            <Box
              sx={{ display: "flex", justifyContent: "center", gap: 1, my: 3 }}
            >
              {[0, 1, 2, 3, 4, 5].map((index) => (
                <TextField
                  key={index}
                  inputRef={(el) => (inputRefs.current[index] = el)}
                  value={formik.values.otp[index] || ""}
                  onChange={(e) => handleOtpChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  onPaste={handlePaste}
                  inputProps={{
                    maxLength: 1,
                    style: {
                      textAlign: "center",
                      fontSize: "18px",
                      border: "none",
                      color: "var(--header__color)",
                    },
                  }}
                  sx={{
                    width: "45px",
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": {
                        borderColor: "var(--otp_box_border)",
                        borderWidth: "2px",
                      },
                      "&:hover fieldset": {
                        borderColor: "var(--otp_box_border)",
                        borderWidth: "2px",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "var(--otp_box_border)",
                        borderWidth: "2px",
                      },
                    },
                    "& .MuiInputBase-input": {
                      textAlign: "center",
                      padding: "10px 0",
                      color: "var(--header__color)",
                    },
                  }}
                  autoFocus={index === 0}
                />
              ))}
            </Box>

            {formik.touched.otp && formik.errors.otp && (
              <div
                style={{
                  color: "red",
                  fontSize: "14px",
                  textAlign: "center",
                  marginBottom: "10px",
                }}
              >
                {formik.errors.otp}
              </div>
            )}

            <div
              className="otp-options"
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Button
                type="submit"
                backgroundColor="#6860EE"
                width="70%"
                label={isLoading ? "Verifying..." : "Verify OTP"}
                disabled={isLoading || formik.values.otp.length !== 6}
              />
            </div>
          </form>

          <div style={{ textAlign: "center", marginTop: "15px" }}>
            <button
              onClick={handleResendOTP}
              disabled={isResendDisabled}
              style={{
                background: "none",
                border: "none",
                color: isResendDisabled ? "#999" : "#6860EE",
                cursor: isResendDisabled ? "not-allowed" : "pointer",
                fontSize: "14px",
              }}
            >
              {isResendDisabled
                ? `Resend OTP in ${resendTimer}s`
                : "Resend OTP"}
            </button>
            <p style={{ fontSize: "13px", color: "#999", marginTop: "5px" }}>
              The OTP will expire after 5 minutes. You can request a new one
              once it expires.
            </p>
          </div>

          <div
            className="back-text"
            style={{ textAlign: "center", marginTop: "20px" }}
          >
            <span
              onClick={handleBackToLogin}
              style={{
                color: "#6860EE",
                cursor: "pointer",
                fontSize: "14px",
              }}
            >
              Back
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default VerifyOtp;
